import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getThreadFull, generateStrategyBrief, exportBriefForChatGPT, updateActionStatus, approveDraft, rejectDraft, getStrategyPrompt } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { formatDateTime, getStatusColor, truncateText } from '../lib/utils';
import { ArrowLeft, FileText, CheckSquare, MessageSquare, Lightbulb, Copy, Sparkles, Users, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const ThreadDetailPage = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generatingBrief, setGeneratingBrief] = useState(false);

  const fetchData = async () => {
    try {
      const response = await getThreadFull(id);
      setData(response.data);
    } catch (error) {
      toast.error('Failed to load thread');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const handleGenerateBrief = async () => {
    setGeneratingBrief(true);
    try {
      await generateStrategyBrief(id);
      toast.success('Strategy brief generated');
      fetchData();
    } catch (error) {
      toast.error('Failed to generate brief');
    } finally {
      setGeneratingBrief(false);
    }
  };

  const handleCopyForChatGPT = async (briefId) => {
    try {
      const response = await exportBriefForChatGPT(briefId);
      await navigator.clipboard.writeText(response.data.export_text);
      toast.success('Copied to clipboard');
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  const handleCopyStrategyPrompt = async () => {
    try {
      const response = await getStrategyPrompt(id);
      await navigator.clipboard.writeText(response.data.strategy_prompt);
      toast.success('Strategy prompt copied to clipboard - paste into ChatGPT');
    } catch (error) {
      toast.error('Failed to generate strategy prompt');
    }
  };

  const handleCompleteAction = async (actionId) => {
    try {
      await updateActionStatus(actionId, 'Completed');
      toast.success('Action completed');
      fetchData();
    } catch (error) {
      toast.error('Failed to update action');
    }
  };

  const handleApproveDraft = async (draftId) => {
    try {
      await approveDraft(draftId);
      toast.success('Draft approved');
      fetchData();
    } catch (error) {
      toast.error('Failed to approve draft');
    }
  };

  const handleRejectDraft = async (draftId) => {
    try {
      await rejectDraft(draftId);
      toast.success('Draft rejected');
      fetchData();
    } catch (error) {
      toast.error('Failed to reject draft');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64 text-zinc-500 font-inter">Loading...</div>;
  }

  if (!data) {
    return <div className="text-center py-8 text-zinc-500">Thread not found</div>;
  }

  const { thread, evidence_records, action_items, draft_messages, strategy_briefs, funnel_items } = data;

  return (
    <div className="space-y-6" data-testid="thread-detail-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <Link to="/threads" className="text-sm text-zinc-500 hover:text-zinc-700 flex items-center gap-1 mb-2">
            <ArrowLeft className="w-3 h-3" /> Back to Threads
          </Link>
          <div className="flex items-center gap-2">
            <h1 className="font-manrope text-xl font-bold text-zinc-900">{thread.name}</h1>
            <Badge className={getStatusColor(thread.status)}>{thread.status}</Badge>
          </div>
          <p className="text-sm text-zinc-500 font-inter mt-1">{thread.objective}</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline"
            onClick={handleCopyStrategyPrompt}
            className="rounded-sm"
            data-testid="copy-strategy-prompt-btn"
          >
            <ExternalLink className="w-4 h-4 mr-1" />
            Copy Prompt for ChatGPT
          </Button>
          <Button 
            onClick={handleGenerateBrief} 
            disabled={generatingBrief}
            className="rounded-sm"
            data-testid="generate-strategy-btn"
          >
            <Sparkles className="w-4 h-4 mr-1" />
            {generatingBrief ? 'Generating...' : 'Generate Strategy Brief'}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="conversations" className="w-full">
        <TabsList className="border-b border-zinc-200 w-full justify-start rounded-none bg-transparent p-0">
          <TabsTrigger value="conversations" className="rounded-none border-b-2 border-transparent data-[state=active]:border-zinc-900">
            <FileText className="w-4 h-4 mr-1" /> Evidence ({evidence_records.length})
          </TabsTrigger>
          <TabsTrigger value="actions" className="rounded-none border-b-2 border-transparent data-[state=active]:border-zinc-900">
            <CheckSquare className="w-4 h-4 mr-1" /> Actions ({action_items.length})
          </TabsTrigger>
          <TabsTrigger value="drafts" className="rounded-none border-b-2 border-transparent data-[state=active]:border-zinc-900">
            <MessageSquare className="w-4 h-4 mr-1" /> Drafts ({draft_messages.length})
          </TabsTrigger>
          <TabsTrigger value="strategy" className="rounded-none border-b-2 border-transparent data-[state=active]:border-zinc-900">
            <Lightbulb className="w-4 h-4 mr-1" /> Strategy ({strategy_briefs.length})
          </TabsTrigger>
          <TabsTrigger value="funnel" className="rounded-none border-b-2 border-transparent data-[state=active]:border-zinc-900">
            <Users className="w-4 h-4 mr-1" /> Funnel ({funnel_items.length})
          </TabsTrigger>
        </TabsList>

        {/* Evidence Records */}
        <TabsContent value="conversations" className="pt-4">
          {evidence_records.length === 0 ? (
            <p className="text-zinc-500 font-inter text-sm">No conversations recorded yet. Use Intake to add.</p>
          ) : (
            <div className="space-y-4">
              {evidence_records.map((record) => (
                <Card key={record.id} className="border border-zinc-200 shadow-none rounded-sm">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="font-inter text-sm font-medium">
                        {record.channel} • {formatDateTime(record.captured_at_timestamp)}
                      </CardTitle>
                      <div className="flex items-center gap-2 text-xs">
                        <Badge variant="outline" className="font-mono text-xs">{record.confidentiality}</Badge>
                        {record.legal_hold && <Badge className="bg-red-500">Legal Hold</Badge>}
                      </div>
                    </div>
                    <p className="text-xs text-zinc-500 font-inter">
                      Participants: {record.participants?.join(', ')}
                    </p>
                  </CardHeader>
                  <CardContent>
                    <pre className="text-sm font-mono bg-zinc-50 p-3 rounded-sm whitespace-pre-wrap max-h-48 overflow-auto">
                      {record.transcript_text}
                    </pre>
                    <p className="text-xs text-zinc-400 font-mono mt-2">Hash: {truncateText(record.transcript_hash, 32)}...</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Action Items */}
        <TabsContent value="actions" className="pt-4">
          {action_items.length === 0 ? (
            <p className="text-zinc-500 font-inter text-sm">No action items yet.</p>
          ) : (
            <div className="space-y-2">
              {action_items.map((action) => (
                <div key={action.id} className="flex items-center justify-between border border-zinc-200 p-3 rounded-sm">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Badge className={`text-xs ${getStatusColor(action.priority)}`}>{action.priority}</Badge>
                      <span className="font-inter text-sm">{action.description}</span>
                    </div>
                    <p className="text-xs text-zinc-500 font-inter mt-1">
                      Owner: {action.owner} • Due: {action.due_date ? formatDateTime(action.due_date) : 'No date'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={getStatusColor(action.status)}>{action.status}</Badge>
                    {action.status !== 'Completed' && (
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="h-7 text-xs rounded-sm"
                        onClick={() => handleCompleteAction(action.id)}
                      >
                        Complete
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Draft Messages */}
        <TabsContent value="drafts" className="pt-4">
          {draft_messages.length === 0 ? (
            <p className="text-zinc-500 font-inter text-sm">No draft messages yet.</p>
          ) : (
            <div className="space-y-3">
              {draft_messages.map((draft) => (
                <Card key={draft.id} className="border border-zinc-200 shadow-none rounded-sm">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="outline" className="text-xs">{draft.channel}</Badge>
                          <Badge className={getStatusColor(draft.approval_status)}>{draft.approval_status}</Badge>
                          {draft.is_internal && <Badge className="bg-blue-100 text-blue-800 text-xs">Internal</Badge>}
                        </div>
                        <p className="font-medium font-inter text-sm">To: {draft.recipient}</p>
                        {draft.subject && <p className="text-sm text-zinc-600 font-inter">Subject: {draft.subject}</p>}
                        <p className="text-sm text-zinc-500 font-inter mt-2 whitespace-pre-wrap">{draft.body}</p>
                      </div>
                      {draft.approval_status === 'Pending' && (
                        <div className="flex flex-col gap-1">
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="h-7 text-xs rounded-sm"
                            onClick={() => handleApproveDraft(draft.id)}
                          >
                            Approve
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="h-7 text-xs rounded-sm text-red-600"
                            onClick={() => handleRejectDraft(draft.id)}
                          >
                            Reject
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Strategy Briefs */}
        <TabsContent value="strategy" className="pt-4">
          {strategy_briefs.length === 0 ? (
            <div className="text-center py-8">
              <Lightbulb className="w-8 h-8 text-zinc-300 mx-auto mb-2" />
              <p className="text-zinc-500 font-inter text-sm">No strategy briefs yet.</p>
              <Button onClick={handleGenerateBrief} className="mt-4 rounded-sm" disabled={generatingBrief}>
                <Sparkles className="w-4 h-4 mr-1" /> Generate Strategy Brief
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {strategy_briefs.map((brief) => (
                <Card key={brief.id} className="border border-zinc-200 shadow-none rounded-sm">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="font-inter text-sm font-medium">
                        Strategy Brief • {formatDateTime(brief.generated_at)}
                      </CardTitle>
                      <div className="flex items-center gap-2">
                        <Badge className={getStatusColor(brief.status)}>{brief.status}</Badge>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="h-7 text-xs rounded-sm"
                          onClick={() => handleCopyForChatGPT(brief.id)}
                          data-testid={`copy-brief-${brief.id}`}
                        >
                          <Copy className="w-3 h-3 mr-1" /> Copy for ChatGPT
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <h4 className="font-medium font-inter text-sm mb-1">Context Snapshot</h4>
                      <p className="text-sm text-zinc-600 font-inter">{brief.context_snapshot}</p>
                    </div>
                    {brief.strategic_options?.length > 0 && (
                      <div>
                        <h4 className="font-medium font-inter text-sm mb-1">Strategic Options</h4>
                        <ul className="list-disc list-inside text-sm text-zinc-600 font-inter space-y-1">
                          {brief.strategic_options.map((opt, i) => <li key={i}>{opt}</li>)}
                        </ul>
                      </div>
                    )}
                    {brief.suggested_objectives?.length > 0 && (
                      <div>
                        <h4 className="font-medium font-inter text-sm mb-1">Suggested Objectives</h4>
                        <ul className="list-disc list-inside text-sm text-zinc-600 font-inter space-y-1">
                          {brief.suggested_objectives.map((obj, i) => <li key={i}>{obj}</li>)}
                        </ul>
                      </div>
                    )}
                    {brief.risks_and_tradeoffs?.length > 0 && (
                      <div>
                        <h4 className="font-medium font-inter text-sm mb-1">Risks & Tradeoffs</h4>
                        <ul className="list-disc list-inside text-sm text-zinc-600 font-inter space-y-1">
                          {brief.risks_and_tradeoffs.map((risk, i) => <li key={i}>{risk}</li>)}
                        </ul>
                      </div>
                    )}
                    <p className="text-xs text-zinc-400 font-inter italic">
                      This is advisory only. All decisions require human review and approval.
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Funnel Items */}
        <TabsContent value="funnel" className="pt-4">
          {funnel_items.length === 0 ? (
            <p className="text-zinc-500 font-inter text-sm">No funnel items linked to this thread.</p>
          ) : (
            <div className="space-y-2">
              {funnel_items.map((item) => (
                <div key={item.id} className="flex items-center justify-between border border-zinc-200 p-3 rounded-sm">
                  <div>
                    <p className="font-medium font-inter text-sm">{item.party}</p>
                    <p className="text-xs text-zinc-500 font-inter">
                      {item.funnel_type} • Stage: {item.stage}
                    </p>
                  </div>
                  {item.next_action_date && (
                    <p className="text-xs text-zinc-500 font-mono">
                      Next: {formatDateTime(item.next_action_date)}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ThreadDetailPage;
